import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '../components/AppShell';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Search, Package, Plus, Loader2, Trash2 } from 'lucide-react';
import { listarEnviosIntegracao, listarEnvios, etiquetaEnvio, cancelarEnvio, criarLinkEtiqueta, enviarEtiquetaEmail, etiquetasLote, excluirEnviosCancelados } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const statusColors = {
  'Em trânsito': 'bg-blue-100 text-blue-700',
  'Entregue': 'bg-emerald-100 text-emerald-700',
  'Aguardando postagem': 'bg-amber-100 text-amber-700',
  'Cancelado': 'bg-red-100 text-red-700',
};

const FORMATOS = [
  ['10x15', '10 x 15 cm', 'Impressora térmica de etiquetas'],
  ['10x8', '10 x 8 cm', 'Etiqueta pequena (2 páginas)'],
  ['a4', 'A4', 'Impressora comum (folha A4)'],
];

const lerFormato = () => { try { return localStorage.getItem('inn_formato_etiqueta') || '10x15'; } catch { return '10x15'; } };

const msgDe = async (e, padrao) => {
  try {
    const d = e?.response?.data;
    if (d instanceof Blob) return JSON.parse(await d.text()).detail || padrao;
    return d?.detail && typeof d.detail === 'string' ? d.detail : padrao;
  } catch { return padrao; }
};

const baixarBlob = (blob, nome) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

const ativo = (s) => s.prepostagem_id && s.status !== 'Cancelado';

const ShipmentsPage = () => {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [acao, setAcao] = useState(null);
  const [menu, setMenu] = useState(null);
  const [formato, setFormato] = useState(lerFormato);
  const [erro, setErro] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [emailDlg, setEmailDlg] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailErro, setEmailErro] = useState(null);

  const [aba, setAba] = useState('meus');
  const integracao = aba === 'integracao';
  const ehAdmin = !!(user && user.is_admin);
  useEffect(() => {
    setLoading(true); setSel(new Set()); setMenu(null);
    (integracao ? listarEnviosIntegracao() : listarEnvios()).then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, [integracao]);

  useEffect(() => {
    if (!menu) return undefined;
    const fechar = (e) => { if (!e.target.closest('[data-menu-etiqueta]')) setMenu(null); };
    document.addEventListener('click', fechar);
    return () => document.removeEventListener('click', fechar);
  }, [menu]);

  const escolherFormato = (f) => { setFormato(f); try { localStorage.setItem('inn_formato_etiqueta', f); } catch { /* noop */ } };

  const executar = async (s, fn, padrao) => {
    setAcao(s?.id || 'lote'); setErro(null); setAviso(null);
    try { await fn(); } catch (e) { setErro(await msgDe(e, padrao)); } finally { setAcao(null); }
  };

  const abrir = (s) => { setMenu(null); return executar(s, async () => { window.open(URL.createObjectURL(await etiquetaEnvio(s.id, formato)), '_blank'); }, 'Não foi possível gerar a etiqueta.'); };
  const baixar = (s) => { setMenu(null); return executar(s, async () => { baixarBlob(await etiquetaEnvio(s.id, formato), `etiqueta-${formato}-${s.codigo_objeto || s.id}.pdf`); }, 'Não foi possível baixar a etiqueta.'); };
  const copiarLink = (s) => {
    setMenu(null);
    return executar(s, async () => {
      const r = await criarLinkEtiqueta(s.id, formato);
      try { await navigator.clipboard.writeText(r.url); setAviso(`Link copiado. Vale por ${r.horas} horas e só abre esta etiqueta.`); } catch { window.prompt('Copie o link (vale por 48 horas):', r.url); }
    }, 'Não foi possível criar o link.');
  };

  const abrirEmail = (s) => { setMenu(null); setEmailErro(null); setEmailDlg({ envio: s, para: user?.email || '' }); };
  const enviarEmail = async (e) => {
    e.preventDefault();
    setEmailBusy(true); setEmailErro(null);
    try {
      await enviarEtiquetaEmail(emailDlg.envio.id, emailDlg.para, formato);
      setAviso(`Etiqueta enviada para ${emailDlg.para}.`);
      setEmailDlg(null);
    } catch (err) { setEmailErro(await msgDe(err, 'Não foi possível enviar o e-mail.')); } finally { setEmailBusy(false); }
  };

  const lote = (baixarArquivo) => executar(null, async () => {
    const r = await etiquetasLote([...sel], formato);
    if (baixarArquivo) baixarBlob(r.blob, `etiquetas-${formato}-${r.incluidas}-envios.pdf`);
    else window.open(URL.createObjectURL(r.blob), '_blank');
    setAviso(`${r.incluidas} etiqueta(s) no PDF${r.ignoradas ? ` (${r.ignoradas} ignorada(s): canceladas ou indisponíveis)` : ''}.`);
  }, 'Não foi possível montar o PDF do lote.');

  const excluirCancelados = async () => {
    const qtd = items.filter((s) => s.status === 'Cancelado').length;
    if (!window.confirm(`Excluir ${qtd} envio(s) cancelado(s) da lista? Isso só remove da listagem — não mexe em saldo nem nos Correios.`)) return;
    setAcao('excluir-cancelados'); setErro(null); setAviso(null);
    try {
      const r = await excluirEnviosCancelados();
      setItems(await listarEnvios());
      setAviso(`${r.excluidos} envio(s) cancelado(s) excluído(s).`);
    } catch (e) { setErro(await msgDe(e, 'Não foi possível excluir os envios cancelados.')); } finally { setAcao(null); }
  };

  const cancelar = async (s) => {
    if (!window.confirm('Cancelar este envio? O valor volta para a sua carteira.')) return;
    setAcao(s.id); setErro(null); setAviso(null);
    try {
      await cancelarEnvio(s.id);
      setItems(await listarEnvios());
      setSel((cur) => { const n = new Set(cur); n.delete(s.id); return n; });
      await refreshUser();
    } catch (e) { setErro(e?.response?.data?.detail || 'Não foi possível cancelar.'); } finally { setAcao(null); }
  };

  const filtered = useMemo(() => items.filter((s) =>
    s.id?.toLowerCase().includes(search.toLowerCase()) ||
    s.recipient_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.cidade_destino?.toLowerCase().includes(search.toLowerCase()) ||
    s.codigo_objeto?.toLowerCase().includes(search.toLowerCase())
  ), [items, search]);

  const elegiveis = filtered.filter(ativo);
  const todosMarcados = elegiveis.length > 0 && elegiveis.every((s) => sel.has(s.id));
  const alternar = (id) => setSel((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const alternarTodos = () => setSel(todosMarcados ? new Set() : new Set(elegiveis.map((s) => s.id)));

  const Formatos = ({ compacto }) => (
    <div className={`flex gap-1 ${compacto ? '' : 'flex-wrap'}`}>
      {FORMATOS.map(([f, t]) => (
        <button key={f} type="button" onClick={() => escolherFormato(f)}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${formato === f ? 'bg-[#2A5BC7] text-white border-[#2A5BC7]' : 'border-slate-200 text-slate-600 hover:border-[#2A5BC7]'}`}>{t}</button>
      ))}
    </div>
  );

  return (
    <AppShell title="Meus envios" subtitle="Gerencie todos os seus envios">
      {ehAdmin && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="inline-flex bg-slate-100 rounded-full p-1">
            {[['meus', 'Meus envios'], ['integracao', 'Envios da integração']].map(([k, rot]) => (
              <button key={k} type="button" onClick={() => { setAba(k); setSearch(''); }} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${aba === k ? 'bg-white text-[#2A5BC7] shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>{rot}</button>
            ))}
          </div>
          <Link to="/relatorios?escopo=integracao&periodo=mes"><Button variant="outline" className="rounded-full h-9 px-4 text-sm bg-white">Relatório de gastos da integração</Button></Link>
        </div>
      )}
      {integracao && <p className="text-sm text-slate-500 mb-3">Etiquetas emitidas pelo sistema de gestão (gerenciadorinnova.app). Para cancelar, use o próprio Gerenciador.</p>}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-100">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Buscar por código, destinatário, cidade..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <div className="flex items-center gap-2">
            {!integracao && items.some((s) => s.status === 'Cancelado') && (
              <Button
                variant="outline"
                disabled={acao === 'excluir-cancelados'}
                onClick={excluirCancelados}
                className="rounded-full h-10 px-4 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> {acao === 'excluir-cancelados' ? 'Excluindo...' : 'Excluir cancelados'}
              </Button>
            )}
            <Link to="/calcular"><Button className="inn-btn-primary rounded-full h-10 px-5"><Plus className="w-4 h-4 mr-1" /> Novo envio</Button></Link>
          </div>
        </div>

        {sel.size > 0 && (
          <div className="px-5 py-3 bg-[#EAF1FF] border-b border-slate-100 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-slate-900">{sel.size} selecionado(s)</span>
            <Formatos compacto />
            <Button disabled={acao === 'lote'} onClick={() => lote(false)} className="inn-btn-primary rounded-full h-9 px-4 text-xs">{acao === 'lote' ? 'Montando PDF...' : 'Abrir PDF do lote'}</Button>
            <Button disabled={acao === 'lote'} onClick={() => lote(true)} variant="outline" className="rounded-full h-9 px-4 text-xs bg-white">Baixar PDF do lote</Button>
            <button onClick={() => setSel(new Set())} className="text-xs text-slate-500 hover:underline">Limpar seleção</button>
          </div>
        )}
        {erro && <p className="px-5 py-3 text-sm text-red-600 border-b border-slate-100">{erro}</p>}
        {aviso && <p className="px-5 py-3 text-sm text-emerald-700 bg-emerald-50 border-b border-slate-100">{aviso}</p>}

        {loading ? (
          <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-slate-400">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-semibold text-slate-700">Nenhum envio ainda</p>
            <p className="text-sm mt-1">Crie seu primeiro envio para começar a economizar.</p>
            <Link to="/calcular"><Button className="mt-5 inn-btn-primary rounded-full">Criar primeiro envio</Button></Link>
          </div>
        ) : (
          <div className="overflow-x-auto pb-24">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 w-8"><input type="checkbox" checked={todosMarcados} onChange={alternarTodos} aria-label="Selecionar todos" /></th>
                  <th className="text-left px-5 py-3 font-semibold">Data</th>
                  <th className="text-left px-5 py-3 font-semibold">Destinatário</th>
                  <th className="text-left px-5 py-3 font-semibold">Destino</th>
                  <th className="text-left px-5 py-3 font-semibold">Serviço</th>
                  <th className="text-left px-5 py-3 font-semibold">Previsão de entrega</th>
                  <th className="text-right px-5 py-3 font-semibold">Valor</th>
                  <th className="text-right px-5 py-3 font-semibold">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-4">{ativo(s) && !integracao && <input type="checkbox" checked={sel.has(s.id)} onChange={() => alternar(s.id)} aria-label="Selecionar envio" />}</td>
                    <td className="px-5 py-4 text-slate-600 text-xs">{new Date(s.created_at).toLocaleDateString('pt-BR')}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-slate-900 font-medium">{s.recipient_name}</span>
                        <Badge className={`${statusColors[s.status] || 'bg-slate-100 text-slate-700'} border-0 font-medium`}>{s.status}</Badge>
                      </div>
                      {integracao && s.pedido_gestao && <div className="text-xs text-slate-500 mt-0.5">Pedido {s.pedido_gestao} no Gerenciador{s.emitido_por ? ` · emitido por ${s.emitido_por}` : ''}</div>}
                    </td>
                    <td className="px-5 py-4 text-slate-600">{s.cidade_destino}/{s.uf_destino}</td>
                    <td className="px-5 py-4 text-slate-600">{s.servico_nome || s.servico}</td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">
                      {s.status === 'Entregue' ? <span className="text-emerald-700 font-medium">Entregue</span>
                        : (s.status === 'Cancelado' || s.status === 'Cancelando') ? '–'
                        : s.previsao_entrega ? (<><span className="font-medium text-slate-800">{s.previsao_entrega.split('-').reverse().join('/')}</span>{s.prazo_dias ? <div className="text-xs text-slate-400">{s.prazo_dias} dias úteis</div> : null}</>)
                        : <span className="text-slate-400 text-xs">a confirmar</span>}
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-slate-900 whitespace-nowrap">R$ {Number(s.valor).toFixed(2).replace('.', ',')}</td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      {s.codigo_objeto && <p className="text-[11px] mb-1"><Link to={`/rastreio?codigo=${s.codigo_objeto}`} className="text-slate-500 hover:text-[#2A5BC7] underline decoration-dotted" title="Rastrear este envio">{s.codigo_objeto}</Link></p>}
                      {ativo(s) && (
                        <span className="relative inline-block mr-3" data-menu-etiqueta>
                          <button disabled={acao === s.id} onClick={() => setMenu(menu === s.id ? null : s.id)} className="text-xs font-semibold text-[#2A5BC7] hover:underline">{acao === s.id ? 'Gerando...' : 'Etiqueta ▾'}</button>
                          {menu === s.id && (
                            <div className="absolute right-0 top-6 z-20 w-72 bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-left">
                              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Tamanho</p>
                              <Formatos />
                              <p className="text-[11px] text-slate-500 mt-1.5 mb-2">{FORMATOS.find((x) => x[0] === formato)?.[2]}</p>
                              <div className="grid grid-cols-2 gap-2">
                                <button onClick={() => abrir(s)} className="px-2 py-2 rounded-lg bg-[#2A5BC7] text-white text-xs font-semibold hover:opacity-90">Abrir / imprimir</button>
                                <button onClick={() => baixar(s)} className="px-2 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50">Baixar PDF</button>
                                <button onClick={() => copiarLink(s)} className="px-2 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50">Copiar link</button>
                                <button onClick={() => abrirEmail(s)} className="px-2 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50">Enviar por e-mail</button>
                              </div>
                            </div>
                          )}
                        </span>
                      )}
                      {s.status === 'Aguardando postagem' && !integracao && (
                        <button disabled={acao === s.id} onClick={() => cancelar(s)} className="text-xs font-semibold text-red-600 hover:underline">Cancelar</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!emailDlg} onOpenChange={(o) => { if (!o) setEmailDlg(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar etiqueta por e-mail</DialogTitle>
            <DialogDescription>A etiqueta segue em PDF, no tamanho {FORMATOS.find((x) => x[0] === formato)?.[1]}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={enviarEmail} className="space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">E-mail de destino</span>
              <Input className="mt-1" type="email" required value={emailDlg?.para || ''} onChange={(e) => setEmailDlg({ ...emailDlg, para: e.target.value })} />
            </label>
            {emailErro && <p className="text-sm text-red-600">{emailErro}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEmailDlg(null)}>Cancelar</Button>
              <Button type="submit" disabled={emailBusy} className="inn-btn-primary rounded-full px-6">{emailBusy ? 'Enviando...' : 'Enviar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default ShipmentsPage;
